import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { money, FALLBACK_IMG } from "@/lib/api";
import Stars from "@/components/Stars";
import { toast } from "sonner";

export default function ProductCard({ product, index = 0 }) {
  const { add } = useCart();
  const soldOut = product.in_stock === false || product.stock_count === 0;
  const lowStock =
    !soldOut &&
    typeof product.stock_count === "number" &&
    product.stock_count > 0 &&
    product.stock_count <= 5;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, delay: (index % 4) * 0.07, ease: [0.16, 1, 0.3, 1] }}
      onMouseMove={onMove}
      className="spotlight group relative border border-border bg-card flex flex-col transition-colors duration-500 hover:border-primary/40"
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`} className="relative block overflow-hidden aspect-square bg-secondary">
        <img
          src={product.image || FALLBACK_IMG}
          alt={product.name}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.07] ${soldOut ? "grayscale opacity-60" : ""}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <span className="absolute top-3 left-3 z-[3] font-mono-label text-[10px] px-2 py-1 bg-background/80 glass border border-border">
          {product.category}
        </span>
        {soldOut && (
          <span data-testid={`out-of-stock-${product.slug}`} className="absolute top-3 right-3 z-[3] font-mono-label text-[10px] px-2 py-1 bg-destructive text-destructive-foreground">
            SOLD OUT
          </span>
        )}
        {!soldOut && product.type === "digital" && (
          <span className="absolute top-3 right-3 z-[3] font-mono-label text-[10px] px-2 py-1 bg-primary text-primary-foreground">
            {product.badges?.includes("Best Value") ? "BUNDLE" : "PDF"}
          </span>
        )}
        {lowStock && (
          <span
            data-testid={`low-stock-${product.slug}`}
            className="absolute top-3 left-1/2 -translate-x-1/2 z-[3] font-mono-label text-[10px] px-2.5 py-1 bg-amber-500 text-black shadow-[0_0_0_1px_rgba(0,0,0,0.06)] animate-pulse whitespace-nowrap"
          >
            ONLY {product.stock_count} LEFT
          </span>
        )}
        {!soldOut && (
          <button
            data-testid={`quick-add-${product.slug}`}
            onClick={(e) => { e.preventDefault(); add(product); toast.success(`${product.name} added to cart`); }}
            className="absolute bottom-0 left-0 right-0 z-[3] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] bg-primary text-primary-foreground font-mono-label text-[11px] py-3.5 flex items-center justify-center gap-2 hover:brightness-110"
          >
            <Plus className="w-3.5 h-3.5" /> Quick Add
          </button>
        )}
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <Link to={`/product/${product.slug}`}>
          <h3 className="font-display text-xl leading-tight font-medium group-hover:text-primary transition-colors">
            {product.name}
          </h3>
        </Link>
        {product.rating_count > 0 && (
          <div className="flex items-center gap-1.5 mt-1.5" data-testid={`rating-${product.slug}`}>
            <Stars value={product.rating_avg} size={13} />
            <span className="font-mono text-[11px] text-muted-foreground">{product.rating_avg} ({product.rating_count})</span>
          </div>
        )}
        <p className="text-sm text-muted-foreground mt-2 line-clamp-2 flex-1">{product.short}</p>

        <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
          <span className="font-mono text-lg font-semibold">{money(product.price)}</span>
          {soldOut ? (
            <span className="font-mono-label text-[11px] text-muted-foreground px-4 py-2.5">Out of Stock</span>
          ) : (
            <button
              data-testid={`add-to-cart-${product.slug}`}
              onClick={() => { add(product); toast.success(`${product.name} added to cart`); }}
              className="flex items-center gap-1.5 bg-primary hover:brightness-110 text-primary-foreground font-mono-label text-[11px] px-4 py-2.5 rounded-full transition-[filter,transform] active:scale-95"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
